import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      const user = JSON.parse(userData);
      setUser(user);
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/login', { username, password });
      const { token, user: userData } = response.data;

      // Convert snake_case to camelCase for consistency
      const normalizedUser = {
        id: userData.id,
        username: userData.username,
        email: userData.email,
        fullName: userData.full_name || userData.fullName,
        phone: userData.phone,
        role: userData.role,
        useAutoPriority: userData.use_auto_priority ?? false,
        taskPriorityLowToMedium: userData.task_priority_low_to_medium ?? 1,
        taskPriorityMediumToHigh: userData.task_priority_medium_to_high ?? 3,
        preferences: userData.preferences || {},
      };

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      setUser(normalizedUser);

      // Check attendance status on login
      try {
        const attendanceResponse = await api.get('/attendance/active-shift');
        if (attendanceResponse.data.hasActiveShift && attendanceResponse.data.activeShift) {
          const startTime = new Date(attendanceResponse.data.activeShift.start_time).getTime();
          sessionStorage.setItem('attendance_hasActiveShift', 'true');
          sessionStorage.setItem('attendance_shiftStartTime', startTime.toString());
          sessionStorage.setItem('attendance_workFromHome', attendanceResponse.data.activeShift.work_from_home ? 'true' : 'false');
        } else {
          sessionStorage.removeItem('attendance_hasActiveShift');
          sessionStorage.removeItem('attendance_shiftStartTime');
          sessionStorage.removeItem('attendance_workFromHome');
        }
      } catch (attendanceError) {
        // Silently fail - attendance feature might not be available yet
      }

      return { success: true, user: normalizedUser };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'שגיאת התחברות'
      };
    }
  };

  const loginWithToken = async (token) => {
    try {
      // Store token
      localStorage.setItem('token', token);

      // Fetch user data with the token
      const response = await api.get('/auth/me');
      const userData = response.data;

      // Normalize user data
      const normalizedUser = {
        id: userData.id,
        username: userData.username,
        email: userData.email,
        fullName: userData.full_name || userData.fullName,
        phone: userData.phone,
        role: userData.role,
        useAutoPriority: userData.use_auto_priority ?? false,
        taskPriorityLowToMedium: userData.task_priority_low_to_medium ?? 1,
        taskPriorityMediumToHigh: userData.task_priority_medium_to_high ?? 3,
        preferences: userData.preferences || {},
      };

      localStorage.setItem('user', JSON.stringify(normalizedUser));
      setUser(normalizedUser);

      return { success: true };
    } catch (error) {
      localStorage.removeItem('token');
      return {
        success: false,
        error: error.response?.data?.error || 'שגיאת התחברות'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Clear attendance sessionStorage
    sessionStorage.removeItem('attendance_hasActiveShift');
    sessionStorage.removeItem('attendance_shiftStartTime');
    sessionStorage.removeItem('attendance_workFromHome');

    setUser(null);
  };

  const updateUser = (userData) => {
    // Normalize user data to camelCase
    const normalizedUser = {
      id: userData.id,
      username: userData.username,
      email: userData.email,
      fullName: userData.full_name || userData.fullName,
      phone: userData.phone,
      role: userData.role,
      useAutoPriority: userData.use_auto_priority !== undefined ? userData.use_auto_priority : userData.useAutoPriority,
      taskPriorityLowToMedium: userData.task_priority_low_to_medium !== undefined ? userData.task_priority_low_to_medium : userData.taskPriorityLowToMedium,
      taskPriorityMediumToHigh: userData.task_priority_medium_to_high !== undefined ? userData.task_priority_medium_to_high : userData.taskPriorityMediumToHigh,
      preferences: userData.preferences !== undefined ? userData.preferences : userData.preferences || {},
    };

    localStorage.setItem('user', JSON.stringify(normalizedUser));
    setUser(normalizedUser);
  };

  const updatePreferences = async (newPreferences) => {
    try {
      await api.patch(`/users/${user.id}/preferences`, { preferences: newPreferences });

      // Update local user object
      const updatedUser = {
        ...user,
        preferences: newPreferences,
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      return { success: true };
    } catch (error) {
      console.error('Failed to update preferences:', error);
      return { success: false, error };
    }
  };

  const value = {
    user,
    login,
    loginWithToken,
    logout,
    updateUser,
    updatePreferences,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager' || user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
